import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Search, Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import SearchModal from './SearchModal';

const Navbar = () => {
  const location = useLocation();
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [hoveredLink, setHoveredLink] = useState(null);
  
  const navLinks = [
    { name: 'Home', path: '/' },
    { name: 'Projects', path: '/projects' },
    { name: 'Research & Ideas', path: '/ideas' },
    { name: 'About', path: '/about' }
  ];

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsSearchOpen((prev) => !prev);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  return (
    <>
      <nav className="sticky top-0 z-40 backdrop-blur-md bg-[#090714]/80 border-b border-purple-500/15 transition-all duration-300 shadow-[0_4px_30px_rgba(0,0,0,0.8)]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex flex-row justify-between items-center">
          
          {/* Logo / Brand */}
          <Link 
            to="/" 
            className="group text-lg sm:text-xl font-bold tracking-tight text-white flex items-center gap-1.5 transition-transform duration-200 hover:scale-[1.02]"
          >
            <span className="relative font-serif">
              NotKoneko
              <span className="absolute -bottom-0.5 left-0 w-0 h-0.5 bg-gradient-to-r from-purple-500 to-pink-500 transition-all duration-300 group-hover:w-full" />
            </span>
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-fuchsia-400 to-pink-400 font-mono font-light text-base group-hover:brightness-125 transition-all">
              =^..^=
            </span>
          </Link>
          
          <div className="flex items-center gap-2 sm:gap-6">
            
            {/* Desktop Navigation */}
            <div className="hidden sm:flex items-center gap-1 relative">
              {navLinks.map((link) => (
                <Link 
                  key={link.name} 
                  to={link.path}
                  onMouseEnter={() => setHoveredLink(link.name)}
                  onMouseLeave={() => setHoveredLink(null)}
                  className="relative text-xs sm:text-sm font-medium transition-colors py-2 px-3.5 rounded-xl flex items-center z-10"
                >
                  {/* Hover Indicator */}
                  {hoveredLink === link.name && (
                    <motion.span
                      layoutId="navHover"
                      className="absolute inset-0 bg-purple-500/10 rounded-xl -z-10"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}

                  {/* Active Indicator */}
                  {location.pathname === link.path && (
                    <motion.span
                      layoutId="navActive"
                      className="absolute inset-0 bg-purple-500/15 border border-purple-500/25 rounded-xl -z-10"
                      transition={{ type: "spring", stiffness: 380, damping: 30 }}
                    />
                  )}

                  <span className={`transition-colors duration-200 ${
                    location.pathname === link.path 
                      ? 'text-fuchsia-300 font-bold' 
                      : 'text-gray-400 hover:text-gray-200'
                  }`}>
                    {link.name}
                  </span>
                </Link>
              ))}
            </div>

            {/* Search Trigger */}
            <button
              onClick={() => setIsSearchOpen(true)}
              className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-purple-950/20 hover:bg-purple-900/30 border border-purple-500/20 hover:border-fuchsia-500/40 text-gray-400 hover:text-white transition-all duration-200 group active:scale-95 shadow-sm"
              title="Search website (Ctrl+K)"
              aria-label="Search website"
            >
              <Search className="w-4 h-4 text-purple-400 group-hover:text-fuchsia-400 group-hover:scale-110 transition-all" />
              <span className="hidden sm:inline text-xs font-medium text-gray-400 group-hover:text-gray-200 transition-colors">Search...</span>
              <kbd className="hidden md:inline-block text-[10px] bg-purple-950/50 px-2 py-0.5 rounded-lg text-purple-300/70 border border-purple-500/20 font-mono">⌘K</kbd>
            </button>

            {/* Mobile Menu Button */}
            <button
              onClick={() => setIsMobileMenuOpen((prev) => !prev)}
              className="sm:hidden p-2 rounded-xl bg-purple-950/20 hover:bg-purple-900/30 border border-purple-500/20 text-gray-400 hover:text-fuchsia-300 transition-all active:scale-95"
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <X className="w-4 h-4 text-fuchsia-400" /> : <Menu className="w-4 h-4" />}
            </button>
          </div>
        </div>

        {/* Mobile Dropdown Menu with Framer Motion Animation */}
        <AnimatePresence>
          {isMobileMenuOpen && (
            <motion.div 
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.25, ease: "easeInOut" }}
              className="sm:hidden overflow-hidden border border-purple-500/20 bg-[#090714]/95 backdrop-blur-xl px-4 py-3 rounded-2xl mt-2 flex flex-col gap-1.5 shadow-2xl"
            >
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.path}
                  className={`text-xs font-medium transition-all py-2.5 px-3 rounded-xl flex items-center ${
                    location.pathname === link.path 
                      ? 'text-fuchsia-300 bg-purple-500/15 border border-purple-500/25 font-semibold shadow-inner' 
                      : 'text-gray-400 hover:text-gray-200 hover:bg-white/[0.02]'
                  }`}
                >
                  {link.name}
                </Link>
              ))}
            </motion.div>
          )}
        </AnimatePresence>
      </nav>

      <SearchModal isOpen={isSearchOpen} onClose={() => setIsSearchOpen(false)} />
    </>
  );
};

export default Navbar;
